import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  HeartIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import Swal from 'sweetalert2';
import logo from '../../assets/images/logo.jpeg';

// ─── animation variants (defined outside component to avoid re-creation) ───────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

const fadeRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const slideDown = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 }
};

// ─── advantages data ─────────────────────────────────────────────────────────
const advantages = [
  { icon: '💪', text: 'Modern Equipment' },
  { icon: '👥', text: 'Expert Trainers' },
  { icon: '🌡️', text: 'Sauna & Steam' },
  { icon: '🛍️', text: 'Pro Shop' },
  { icon: '⏱️', text: '24/7 Access' },
  { icon: '🎯', text: 'Free Classes' }
];

// ─── password strength helper ─────────────────────────────────────────────────
const strengthLevels = [
  { label: 'Very Weak',   color: 'bg-red-500',    message: 'Too weak' },
  { label: 'Weak',        color: 'bg-orange-500',  message: 'Add more characters' },
  { label: 'Fair',        color: 'bg-yellow-500',  message: 'Could be stronger' },
  { label: 'Good',        color: 'bg-blue-500',    message: 'Getting better' },
  { label: 'Strong',      color: 'bg-green-500',   message: 'Perfect!' },
  { label: 'Very Strong', color: 'bg-[#800020]',   message: 'Excellent!' }
];

function getPasswordStrength(password) {
  if (!password) return { strength: 0, label: '', color: 'bg-gray-700', message: '', checks: {} };

  const checks = {
    length:    password.length >= 6,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number:    /[0-9]/.test(password),
    special:   /[^A-Za-z0-9]/.test(password)
  };

  const score = Math.min(Object.values(checks).filter(Boolean).length, 5);
  return { strength: score, checks, ...strengthLevels[score] };
}

// ─── reusable input field ─────────────────────────────────────────────────────
function InputField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  Icon,
  focused,
  onFocus,
  onBlur,
  borderClass = '',
  children
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-300 ml-1 mb-1 block">
        {label}
      </label>
      <div
        className={`relative transition-transform duration-300 ${focused ? 'scale-[1.01]' : ''}`}
      >
        {Icon && (
          <Icon
            className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors duration-300 ${
              focused ? 'text-[#800020]' : 'text-gray-500'
            }`}
          />
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          required
          placeholder={placeholder}
          style={{ height: '45px' }}
          className={`w-full pl-9 pr-3 py-2 bg-gray-900 border-2 rounded-lg
            focus:ring-0 outline-none transition-all duration-300
            text-white placeholder-gray-600 text-sm
            focus:bg-gray-800 ${borderClass || 'border-gray-800 focus:border-[#800020]'}`}
        />
        {children}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading]                         = useState(false);
  const [focused, setFocused]                         = useState({
    name: false, email: false, phone: false, password: false, confirmPassword: false
  });

  const { register, googleSignIn } = useAuth();
  const navigate = useNavigate();

  // ── alert helpers ────────────────────────────────────────────────────────────
  const toastOptions = (icon, message) => ({
    position: 'top-end',
    icon,
    title: message,
    showConfirmButton: false,
    timer: 2000,
    backdrop: 'transparent',
    toast: true,
    customClass: {
      popup: `rounded-2xl shadow-2xl border-l-4 ${icon === 'success' ? 'border-green-500' : 'border-red-500'}`,
      title: 'text-gray-800 text-sm font-medium'
    }
  });

  const showSuccessAlert = (msg) => Swal.fire(toastOptions('success', msg));
  const showErrorAlert   = (msg) => Swal.fire(toastOptions('error', msg));

  const showWelcomeDialog = (name) => {
    Swal.fire({
      title: '🎉 Welcome to PowerCore!',
      html: `
        <div class="py-4">
          <div class="w-20 h-20 bg-gradient-to-br from-[#800020] to-[#5a0015] rounded-full
               flex items-center justify-center mx-auto mb-4 shadow-xl">
            <span class="text-3xl text-white">💪</span>
          </div>
          <p class="text-gray-600 text-base mb-2">
            Hello, <span class="font-bold text-[#800020]">${name}</span>!
          </p>
          <p class="text-gray-500 text-sm mb-3">Your account has been created successfully.</p>
          <div class="bg-[#800020]/5 rounded-lg p-3 mb-3">
            <p class="text-xs text-gray-700">📧 Verification email sent to:</p>
            <p class="font-semibold text-[#800020] text-sm">${formData.email}</p>
          </div>
          <p class="text-xs text-gray-500">Please verify your email to access all features</p>
        </div>
      `,
      icon: 'success',
      confirmButtonText: 'Continue to Login',
      confirmButtonColor: '#800020',
      background: '#ffffff',
      customClass: {
        popup: 'rounded-3xl shadow-2xl p-4',
        title: 'text-xl font-bold text-gray-900',
        confirmButton: 'px-6 py-2 rounded-lg font-semibold transition-all duration-300 text-sm'
      }
    }).then(() => navigate('/login'));
  };

  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const setFocus = (field, val) =>
    setFocused((prev) => ({ ...prev, [field]: val }));

  const handleGoogleSignIn = async () => {
    try {
      const result = await googleSignIn();
      if (result.success) {
        showSuccessAlert('Google sign-up successful!');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        showErrorAlert(result.error);
      }
    } catch {
      showErrorAlert('Google sign-up failed');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim())             { showErrorAlert('Name is required');                      return false; }
    if (!formData.email.includes('@'))     { showErrorAlert('Valid email is required');               return false; }
    if (!formData.phone.trim())            { showErrorAlert('Phone number is required');              return false; }
    if (formData.password.length < 6)      { showErrorAlert('Password must be at least 6 characters'); return false; }
    if (formData.password !== formData.confirmPassword) {
      showErrorAlert('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.phone
    );

    if (result.success) {
      showWelcomeDialog(formData.name);
    } else {
      showErrorAlert(result.error || 'Registration failed');
      setLoading(false);
    }
  };

  // ── derived state ─────────────────────────────────────────────────────────────
  const passwordStrength = getPasswordStrength(formData.password);

  const passwordsMatch =
    formData.password && formData.confirmPassword
      ? formData.password === formData.confirmPassword
      : null;

  const confirmBorderClass = formData.confirmPassword
    ? passwordsMatch === false
      ? 'border-red-500 focus:border-red-500'
      : 'border-green-500 focus:border-green-500'
    : 'border-gray-800 focus:border-[#800020]';

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black flex">

      {/* ── Left panel ─────────────────────────────────────────────────────────── */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={logo}
            alt="PowerCore Fitness"
            className="w-full h-full object-cover rounded-r-3xl"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent rounded-r-3xl" />
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="relative z-10 h-full flex flex-col justify-center p-12 text-white"
        >
          {/* Logo */}
          <div className="flex items-center space-x-3 mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-[#800020]/30 rounded-xl blur-xl" />
              <img
                src={logo}
                alt="PowerCore"
                className="h-12 w-auto relative rounded-lg border-2 border-[#800020]/30"
              />
            </div>
            <div>
              <h1 className="text-3xl font-black">
                <span className="text-[#800020]">POWER</span>
                <span className="text-white">CORE</span>
              </h1>
              <p className="text-gray-400 text-xs tracking-wider">FITNESS</p>
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-4xl font-bold mb-4 leading-tight">
            Start Your<br />
            <span className="text-[#800020]">Journey</span> Today
          </h2>
          <p className="text-gray-300 text-base mb-6 max-w-lg">
            Join thousands of members who have transformed their lives with
            PowerCore's premium facilities.
          </p>

          {/* Advantages */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {advantages.map((adv, i) => (
              <motion.div
                key={adv.text}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm
                           rounded-lg p-2 border border-white/20
                           hover:bg-white/20 transition-all duration-300 group"
              >
                <span className="text-xl">{adv.icon}</span>
                <span className="text-xs text-gray-200 group-hover:text-white">
                  {adv.text}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="border-l-4 border-[#800020] pl-3 italic text-gray-300">
            <p className="text-sm">"The best decision I ever made!"</p>
            <p className="text-xs text-[#800020] mt-1">— James K.</p>
          </div>
        </motion.div>
      </div>

      {/* ── Right panel ────────────────────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center p-6">
        <motion.div
          variants={fadeRight}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="mb-6 text-center lg:text-left">
            <h2 className="text-3xl font-bold mb-1 text-white">
              Create <span className="text-[#800020]">Account</span>
            </h2>
            <p className="text-gray-400 text-sm">
              Join PowerCore and transform your life
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">

            {/* Name */}
            <InputField
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              Icon={UserIcon}
              focused={focused.name}
              onFocus={() => setFocus('name', true)}
              onBlur={() => setFocus('name', false)}
            />

            {/* Email */}
            <InputField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              Icon={EnvelopeIcon}
              focused={focused.email}
              onFocus={() => setFocus('email', true)}
              onBlur={() => setFocus('email', false)}
            />

            {/* Phone */}
            <InputField
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+254 712 345 678"
              Icon={PhoneIcon}
              focused={focused.phone}
              onFocus={() => setFocus('phone', true)}
              onBlur={() => setFocus('phone', false)}
            />

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-gray-300 ml-1 mb-1 block">
                Password
              </label>
              <div
                className={`relative transition-transform duration-300 ${
                  focused.password ? 'scale-[1.01]' : ''
                }`}
              >
                <LockClosedIcon
                  className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors duration-300 ${
                    focused.password ? 'text-[#800020]' : 'text-gray-500'
                  }`}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocus('password', true)}
                  onBlur={() => setFocus('password', false)}
                  required
                  placeholder="••••••••"
                  style={{ height: '45px' }}
                  className="w-full pl-9 pr-9 py-2 bg-gray-900 border-2 border-gray-800
                             rounded-lg focus:border-[#800020] focus:bg-gray-800
                             focus:ring-0 outline-none transition-all duration-300
                             text-white placeholder-gray-600 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2
                             text-gray-500 hover:text-[#800020] transition-colors"
                >
                  {showPassword
                    ? <EyeSlashIcon className="h-4 w-4" />
                    : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>

              {/* Strength meter */}
              <AnimatePresence>
                {formData.password && (
                  <motion.div
                    key="strength"
                    variants={slideDown}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="mt-2 space-y-1"
                  >
                    <div className="flex gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                            i < passwordStrength.strength
                              ? passwordStrength.color
                              : 'bg-gray-700'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-400">
                        Strength:{' '}
                        <span className="font-semibold text-[#800020]">
                          {passwordStrength.label}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">{passwordStrength.message}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="text-xs font-medium text-gray-300 ml-1 mb-1 block">
                Confirm Password
              </label>
              <div
                className={`relative transition-transform duration-300 ${
                  focused.confirmPassword ? 'scale-[1.01]' : ''
                }`}
              >
                <LockClosedIcon
                  className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors duration-300 ${
                    focused.confirmPassword ? 'text-[#800020]' : 'text-gray-500'
                  }`}
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onFocus={() => setFocus('confirmPassword', true)}
                  onBlur={() => setFocus('confirmPassword', false)}
                  required
                  placeholder="••••••••"
                  style={{ height: '45px' }}
                  className={`w-full pl-9 pr-9 py-2 bg-gray-900 border-2 rounded-lg
                    focus:ring-0 outline-none transition-all duration-300
                    text-white placeholder-gray-600 text-sm focus:bg-gray-800
                    ${confirmBorderClass}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2
                             text-gray-500 hover:text-[#800020] transition-colors"
                >
                  {showConfirmPassword
                    ? <EyeSlashIcon className="h-4 w-4" />
                    : <EyeIcon className="h-4 w-4" />}
                </button>
              </div>

              {/* Match indicator */}
              <AnimatePresence>
                {formData.confirmPassword && (
                  <motion.div
                    key="match"
                    variants={fadeIn}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="flex items-center space-x-1 mt-1 ml-1"
                  >
                    {passwordsMatch ? (
                      <>
                        <CheckCircleIcon className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-green-500">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-3 w-3 text-red-500" />
                        <span className="text-xs text-red-500">Passwords do not match</span>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Terms */}
            <div className="flex items-start space-x-2 pt-1">
              <input
                id="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-[#800020] focus:ring-[#800020]
                           border-gray-700 rounded bg-gray-900 mt-0.5"
              />
              <label htmlFor="terms" className="text-xs text-gray-400">
                I agree to the{' '}
                <Link to="/terms" className="text-[#800020] font-semibold hover:text-[#5a0015]">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-[#800020] font-semibold hover:text-[#5a0015]">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              disabled={loading}
              style={{ height: '45px' }}
              className="w-full bg-gradient-to-r from-[#800020] to-[#5a0015] text-white
                         py-2 rounded-lg font-semibold shadow-lg shadow-[#800020]/30
                         hover:shadow-xl transition-all duration-300 disabled:opacity-50
                         flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span className="text-sm">Create Account</span>
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </motion.button>

            {/* Google */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="button"
              onClick={handleGoogleSignIn}
              style={{ height: '45px' }}
              className="w-full bg-white text-gray-900 py-2 rounded-lg font-semibold
                         hover:bg-gray-100 transition-all duration-300
                         flex items-center justify-center space-x-2 border-2 border-gray-800"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="text-sm">Continue with Google</span>
            </motion.button>

            {/* Login link */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#800020] font-bold hover:text-[#5a0015] transition-colors"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </form>

          {/* Trust badges */}
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.5 }}
            className="flex justify-center space-x-4 mt-6"
          >
            {[
              { Icon: ShieldCheckIcon, label: 'Secure' },
              { Icon: HeartIcon,       label: 'Free Trial' },
              { Icon: SparklesIcon,    label: 'No Fees' }
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center space-x-1 text-gray-500 text-xs">
                <Icon className="h-3 w-3 text-[#800020]" />
                <span>{label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;